import 'react-loading-skeleton/dist/skeleton.css';

import { useContext, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { endpoints } from '@/API/endpoints';
import EditorRedact from '@/components/common/editor-redact/EditorRedact';
import ErrorComponent from '@/components/common/error-component/ErrorComponent';
import Popup from '@/components/common/popup/Popup';
import JournalItem from '@/components/project-case/project-info/journal-item/JournalItem';
import CrossFill from '@/components/ui/icons/CrossFill';
import PlusFill from '@/components/ui/icons/PlusFill';
import { UserInfoContext } from '@/context/UserInfoContext';
import { useApi } from '@/hooks/useApi/useApi';
import { ApiDataType } from '@/types';

import classes from './Journal.module.scss';

interface JournalProps {
	redaktPermit: boolean;
}

interface JournalItemType {
	author: {
		first_name: string;
		id: number;
		last_name: string;
		photo: string | null;
		position: string;
	};
	date: string;
	id: number;
	project: number;
	text: string;
	error?: string[];
}

const Journal = ({ redaktPermit }: JournalProps) => {
	const [isOpenNewEntry, setIsOpenNewEntry] = useState(false);
	const [newEntryValue, setNewEntryValue] = useState('');
	const [error, setError] = useState<string[] | null>(null);
	const [journalList, setJournalList] = useState<ApiDataType<
		JournalItemType[],
		string[]
	> | null>(null);
	const { apiPost, apiGet, apiDelete, apiPatch } = useApi();
	const { userInfoContext } = useContext(UserInfoContext);

	const projectId = globalThis.location.pathname.split('/projects/')[1];

	const handleEditNote = async (
		noteId: number,
		action: string,
		text?: string
	) => {
		if (action === 'delete') {
			return await deleteNote(noteId);
		} else if (action === 'redact') {
			return await updateNote(noteId, text);
		}
	};

	const updateNote = async (noteId: number, text?: string) => {
		const data = {
			text,
		};
		const params = {
			project: projectId,
		};
		const res = await apiPatch(
			`${endpoints.projectJornal}${noteId}/`,
			data,
			params
		);
		return res;
	};

	const deleteNote = async (noteId: number) => {
		const params = {
			project: projectId,
		};
		const res = await apiDelete(`${endpoints.projectJornal}${noteId}/`, params);
		if (res.errorMessage.length === 0) fetchJornalList();
		return res;
	};

	const handleSaveNewEntry = async () => {
		if (!newEntryValue.trim()) return;

		const res = await apiPost(`${endpoints.projectJornal}`, {
			text: newEntryValue,
			project: projectId,
		});

		if (res.errorMessage.length > 0) {
			setError(res.errorMessage);
		} else {
			setNewEntryValue('');
			setIsOpenNewEntry(false);
			fetchJornalList();
		}
	};

	const handleCancelNewEntry = () => {
		setNewEntryValue('');
		setIsOpenNewEntry(false);
		setError(null);
	};

	const handleEditorChange = (value: string) => {
		setNewEntryValue(value);
		if (error?.length) setError(null);
	};

	const fetchJornalList = async () => {
		const res = await apiGet(`${endpoints.projectJornal}`, {
			project: projectId,
		});
		if (res.data?.data.length) res.data.data.reverse();
		setJournalList({
			data: res.data?.data || [],
			errorMessage: res.errorMessage,
		});
	};

	useEffect(() => {
		fetchJornalList();
	}, []);

	return journalList ? (
		<div className={classes.container}>
			<h3 className={classes.title}>Дневник проекта</h3>
			{redaktPermit && (
				<div className={classes.controls}>
					{isOpenNewEntry ? (
						<button
							type='button'
							title='Закрыть'
							className={classes['close-new-entry']}
							onClick={handleCancelNewEntry}
						>
							<CrossFill clazz={classes['close-new-entry__icon']} />
						</button>
					) : (
						<button
							type='button'
							title='Новая запись'
							className={classes['new-entry']}
							onClick={() => setIsOpenNewEntry(true)}
						>
							<PlusFill clazz={classes['new-entry__icon']} />
						</button>
					)}
				</div>
			)}
			<div className={classes.body}>
				{isOpenNewEntry && (
					<div className={classes['entry']}>
						<p className={classes['entry-title']}>
							<span className={classes['entry-title__date']}>
								{new Intl.DateTimeFormat('ru', {
									year: 'numeric',
									month: 'numeric',
									day: 'numeric',
								}).format(new Date())}
							</span>
							<span className={classes['entry-title__user']}>
								{userInfoContext?.data.first_name}{' '}
								{userInfoContext?.data.last_name}
							</span>
						</p>
						<EditorRedact
							initialValue={newEntryValue}
							onChange={handleEditorChange}
							onSave={handleSaveNewEntry}
							onCancel={handleCancelNewEntry}
							placeholder='Добавьте запись'
							withActions
						/>
						{error?.length && (
							<Popup clazz={classes.popup} clickCallback={() => setError(null)}>
								{error.map((i, idx) => (
									<ErrorComponent key={idx}>{i}</ErrorComponent>
								))}
							</Popup>
						)}
					</div>
				)}
				{journalList.errorMessage.length > 0 ? (
					journalList.errorMessage.map((i, idx) => {
						return <ErrorComponent key={idx}>{i}</ErrorComponent>;
					})
				) : journalList.data.length > 0 ? (
					<div className={classes.list}>
						{journalList.data.map((i: JournalItemType) => (
							<JournalItem
								key={i.id}
								note={i}
								redaktPermit={redaktPermit}
								redactCallback={handleEditNote}
							/>
						))}
					</div>
				) : (
					!isOpenNewEntry && <p className={classes.empty}>Записей нет</p>
				)}
			</div>
		</div>
	) : (
		<Skeleton count={2} style={{ display: 'block', height: '102px' }} />
	);
};

export default Journal;
